import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";
// State
import textStyle from "component/toolManager/textStyle.js";
import { addToolState, getToolState } from "component/toolManager/toolState.js";
import toolStyle from "component/toolManager/toolStyle.js";
import toolColors from "component/toolManager/toolColors.js";
// Manipulators
import { moveNewHandle } from "component/molecules/viewport/core/manipulators/index.js";
// Drawing
import {
  getNewContext,
  draw,
  setShadow,
  drawLine
} from "./../../draw/index.js";
import drawHandles from "./../../draw/drawHandles.js";
import drawLinkedTextBox from "./../../draw/drawLinkedTextBox.js";
import math from "core/math";
import EVENTS from "core/customEvent/events.js";
import { cobbAngleCursor } from "../cursors/index.js";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import { Vector3 } from "three/src/math/Vector3";
function isElement(obj) {
  try {
    //Using W3 DOM2 (works for FF, Opera and Chrome)
    return obj instanceof HTMLElement;
  } catch (e) {
    //Browsers not supporting W3 DOM2 don't have HTMLElement and
    //an exception is thrown and we end up here. Testing some
    //properties that all elements have (works on IE7)
    return (
      typeof obj === "object" &&
      obj.nodeType === 1 &&
      typeof obj.style === "object" &&
      typeof obj.ownerDocument === "object"
    );
  }
}
/**
 * @public
 * @class CobbAngleTool
 * @memberof Tools.Annotation
 * @classdesc Tool for measuring the angle between two straight lines.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class CobbAngleTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "CobbAngle",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: cobbAngleCursor
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
    this.hasIncomplete = false;
  }

  createNewMeasurement(eventData) {
    // Create the measurement data for this tool with the end handle activated
    this.hasIncomplete = true;

    return {
      visible: true,
      active: true,
      color: undefined,
      complete: false,
      value: "",
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true
        },
        start2: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
          drawnIndependently: true
        },
        end2: {
          x: eventData.currentPoints.image.x + 1,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
          drawnIndependently: true
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true
        }
      },
      willbeRemove: true,
      moveCount: 0
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    if (data.visible === false) {
      return false;
    }

    if (this.hasIncomplete) {
      return false;
    }

    return (
      math.line.lineSegemntDistance(
        element,
        data.handles.start,
        data.handles.end,
        coords
      ) < 25 ||
      math.line.lineSegemntDistance(
        element,
        data.handles.start2,
        data.handles.end2,
        coords
      ) < 25
    );
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    const { handleRadius, drawHandlesOnHover } = this.configuration;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    //const context = getNewContext(eventData.canvasContext.canvas);
    const context = getNewContext(eventData.canvas);

    const lineWidth = toolStyle.getToolWidth();
    const font = textStyle.getFont();

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false || data.willbeRemove === true) {
        continue;
      }

      draw(context, context => {
        setShadow(context, this.configuration);

        // Differentiate the color of activation tool
        const color = toolColors.getColorIfActive(data);

        drawLine(
          context,
          eventData.element,
          data.handles.start,
          data.handles.end,
          {
            color
          }
        );

        if (data.complete) {
          drawLine(
            context,
            eventData.element,
            data.handles.start2,
            data.handles.end2,
            {
              color
            }
          );
        }

        // 가이드 선 출력 위치 선정
        const primaryVector = new Vector3(
          data.handles.end.x - data.handles.start.x,
          data.handles.end.y - data.handles.start.y,
          0
        );
        const secondaryVector = new Vector3(
          data.handles.end2.x - data.handles.start2.x,
          data.handles.end2.y - data.handles.start2.y,
          0
        );

        const d = primaryVector.dot(secondaryVector);
        const dx = data.handles.end2.x - data.handles.start2.x;
        const dy = data.handles.end2.y - data.handles.start2.y;

        /// 내적이 0 보다 큰 경우,
        if (d > 0) {
          let guideEndPoint = math.point.copy(data.handles.start);

          guideEndPoint = {
            x: guideEndPoint.x + dx,
            y: guideEndPoint.y + dy
          };
          // Differentiate the color of activation tool
          const guideColor = toolColors.getSecondaryColorIfActive(data);

          drawLine(
            context,
            eventData.element,
            guideEndPoint,
            data.handles.start,
            {
              lineDash: [20, 15],
              color: guideColor
            }
          );
        } else if (d < 0) {
          let guideEndPoint = math.point.copy(data.handles.end);

          guideEndPoint = {
            x: guideEndPoint.x + dx,
            y: guideEndPoint.y + dy
          };

          const guideColor = toolColors.getSecondaryColorIfActive(data);

          drawLine(
            context,
            eventData.element,
            data.handles.end,
            guideEndPoint,
            {
              lineDash: [20, 15],
              color: guideColor
            }
          );
        }

        // Draw the handles
        const handleColor = toolColors.getHandleColorIfActive(data);
        const handleOptions = {
          color: handleColor,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          lineWidth: 3
        };

        drawHandles(context, eventData, data.handles, handleOptions);

        // Draw the text
        context.fillStyle = color;

        const text = data.value;

        if (!data.handles.textBox.hasMoved) {
          const textCoords = {
            x: (data.handles.start.x + data.handles.end.x) / 2,
            y: (data.handles.start.y + data.handles.end.y) / 2 - 10
          };

          context.font = font;
          data.handles.textBox.x = textCoords.x;
          data.handles.textBox.y = textCoords.y;
        }
        const linkColor = toolColors.getLinkColorIfActive(data);
        drawLinkedTextBox(
          context,
          eventData.element,
          data.handles.textBox,
          text,
          data.handles,
          textBoxAnchorPoints,
          color,
          linkColor,
          lineWidth,
          0,
          true
        );
      });
    }

    function textBoxAnchorPoints(handles) {
      return [handles.start, handles.start2, handles.end, handles.end2];
    }
  }

  getIncomplete(target) {
    const toolData = getToolState(target, this.name);

    if (toolData === undefined) {
      return;
    }

    for (let i = 0; i < toolData.data.length; i++) {
      if (toolData.data[i].complete === false) {
        return toolData.data[i];
      }
    }
  }

  addNewMeasurement(evt, interactionType) {
    evt.preventDefault();
    evt.stopPropagation();

    const eventData = evt.detail;

    let measurementData;
    let toMoveHandle;

    // Search for incomplete measurements
    //const element = evt.detail.element;
    const enabledElement = evt.detail.element;
    const element = enabledElement.element;
    const maybePending = this.getIncomplete(element);

    if (maybePending) {
      measurementData = maybePending;
      measurementData.complete = true;
      measurementData.handles.start2 = {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        drawnIndependently: false,
        highlight: true,
        active: false
      };
      measurementData.handles.end2 = {
        x: eventData.currentPoints.image.x,
        y: eventData.currentPoints.image.y,
        drawnIndependently: false,
        highlight: true,
        active: true
      };
      toMoveHandle = measurementData.handles.end2;
      this.hasIncomplete = false;
    } else {
      measurementData = this.createNewMeasurement(eventData);
      addToolState(element, this.name, measurementData);
      toMoveHandle = measurementData.handles.end;
    }

    // Associate this data with this imageId so we can render it and manipulate it
    enabledElement.needsRedraw = true;

    moveNewHandle(
      eventData,
      this.name,
      measurementData,
      toMoveHandle,
      this.options,
      interactionType
    );
  }

  onMeasureModified(ev) {
    let image = null;
    if (isElement(ev.detail.element)) {
      image = getEnabledElement(ev.detail.element).image;
    } else {
      image = getEnabledElement(ev.detail.element.element).image;
    }

    if (ev.detail.toolName !== this.name) {
      return;
    }
    const data = ev.detail.measurementData;

    data.value = calculateValue(data, image);

    function calculateValue(data, image) {
      // Default to isotropic pixel size, update suffix to reflect this
      // const columnPixelSpacing = image.columnPixelSpacing || 1;
      // const rowPixelSpacing = image.rowPixelSpacing || 1;
      const columnPixelSpacing = image.pixelSpacing[1] || 1;
      const rowPixelSpacing = image.pixelSpacing[0] || 1;

      const dx1 =
        (Math.ceil(data.handles.start.x) - Math.ceil(data.handles.end.x)) *
        columnPixelSpacing;
      const dy1 =
        (Math.ceil(data.handles.start.y) - Math.ceil(data.handles.end.y)) *
        rowPixelSpacing;
      const dx2 =
        (Math.ceil(data.handles.start2.x) - Math.ceil(data.handles.end2.x)) *
        columnPixelSpacing;
      const dy2 =
        (Math.ceil(data.handles.start2.y) - Math.ceil(data.handles.end2.y)) *
        rowPixelSpacing;

      let angle = Math.acos(
        Math.abs(
          (dx1 * dx2 + dy1 * dy2) /
            (Math.sqrt(dx1 * dx1 + dy1 * dy1) *
              Math.sqrt(dx2 * dx2 + dy2 * dy2))
        )
      );

      angle *= 180 / Math.PI;

      const rAngle = math.roundToDecimal(angle, 2);

      if (!Number.isNaN(data.rAngle)) {
        return textBoxText(
          rAngle,
          // image.rowPixelSpacing,
          // image.columnPixelSpacing
          image.pixelSpacing[0],
          image.pixelSpacing[1]
        );
      }

      return "";
    }

    function textBoxText(rAngle, rowPixelSpacing, columnPixelSpacing) {
      const suffix =
        !rowPixelSpacing || !columnPixelSpacing ? " (isotropic)" : "";
      const str = "00B0"; // Degrees symbol

      return (
        rAngle.toString() + String.fromCharCode(parseInt(str, 16)) + suffix
      );
    }
  }

  activeCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  passiveCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  enabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  disabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }
}
